import { styled } from '@mui/material/styles';
import { Card, Button, Typography } from '@mui/material';

// Content container with flexbox centering
export const ContentContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    paddingBottom: '100px',
    overflowY: 'auto',
    marginLeft: 70,
    [theme.breakpoints.down('md')]: {
        marginLeft: '50px',
        marginTop: '30px',
    },
    [theme.breakpoints.down('sm')]: {
        minHeight: 'calc(100vh - 56px)',
        paddingBottom: '30px',
        marginTop: '30px',
    },
}));

// Main content section
export const MainContent = styled('div')({
    padding: '20px',
    marginBottom: '5px',
});

export const CenteredCard = styled(Card)(({ theme }) => ({
    width: '100%',
    maxWidth: '1600px',
    padding: '40px',
    marginTop: '15%',
    backgroundColor: '#282828',
    boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'center',
    border: '1px solid #444',
    color: '#fff',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
        backgroundColor: '#383838',
        boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.3)',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        padding: '10px',
        margin: '30%',
    },
    '& .card-header': {
        width: '100%',
        padding: '20px 0',
        borderBottom: '3px solid #444',
        fontSize: '28px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        color: '#b0b0b0',
    },
    '& .control-section': {
        width: '100%',
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: '20px',
        borderTop: '3px solid #444',
        paddingTop: '20px',
        paddingBottom: '20px',
    },
    '& .control-section > div': {
        flex: 1,
        backgroundColor: '#333',
        padding: '30px',
        borderRadius: '6px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
        transition: 'background-color 0.3s ease',
        '&:hover': {
            backgroundColor: '#444',
        },
        borderLeft: '3px solid #444',
    },
    '& .control-button': {
        backgroundColor: '#4CAF50',
        padding: '16px 30px',
        borderRadius: '6px',
        color: '#fff',
        fontWeight: 'bold',
        border: 'none',
        transition: 'background-color 0.3s ease',
        '&:hover': {
            backgroundColor: '#45a049',
        },
    },
    '& .control-input': {
        width: '100%',
        padding: '14px',
        borderRadius: '6px',
        border: '1px solid #666',
        backgroundColor: '#444',
        color: '#fff',
        marginTop: '10px',
        transition: 'background-color 0.3s ease',
        '&:focus': {
            backgroundColor: '#555',
            borderColor: '#4CAF50',
        },
    },
    '& .last-action-box': {
        width: '100%',
        padding: '8px',
        backgroundColor: '#003366',  // Dark blue
        color: '#fff',
        textAlign: 'left',
        borderRadius: '6px',
        marginTop: '20px',
        fontSize: '16px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
        fontWeight: 'bold',
        border: '1px solid #FFD700',  // Yellow border
        display: 'flex',
        alignItems: 'center',
    },

    '& .last-action-text': {
        fontWeight: 'normal',
        color: '#D3D3D3',  // Light gray for the text
        marginRight: '12px',
    },

    '& .last-action-data': {
        fontWeight: 'bold',
        color: '#FFD700',  // Yellow for the data
        fontSize: '16px',
    },
}));


// VP1disk button styles with updated attractive colors and animations
export const VPdiskButton = styled(Button)(({ state }) => ({
    background: state === 'ON'
        ? 'linear-gradient(145deg, #4CAF50, #81C784)' // Green for ON
        : state === 'OFF'
            ? 'linear-gradient(145deg,rgb(221, 230, 235),rgb(161, 168, 172))' // Grey for OFF
            : 'linear-gradient(145deg,rgb(221, 230, 235),rgb(161, 168, 172))',
    color: '#000',
    padding: '14px 28px',
    margin: '5px',
    borderRadius: '10px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
    transition: 'transform 0.2s ease, box-shadow 0.3s ease',
    fontWeight: 'bold',
    fontSize: '1rem',
    '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: state === 'ON'
            ? '0px 6px 12px rgba(150, 193, 199, 0.5)'
            : state === 'OFF'
                ? '0px 6px 12px rgba(96, 96, 96, 0.5)'
                : 'none',
    },
    '&:active': {
        transform: 'scale(0.95)',
    },

    // Responsive styling for tablet and mobile devices
    '@media (max-width: 768px)': {
        padding: '12px 24px',
        fontSize: '0.95rem',
    },
    '@media (max-width: 480px)': {
        padding: '10px 20px',
        fontSize: '0.85rem',
        borderRadius: '8px',
    },
}));

// VP1disk styled typography with a sleek gradient effect
export const VPdiskTypography = styled(Typography)`
  color: #fff;
  font-weight: bold;
  padding: 15px 20px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: linear-gradient(135deg, rgba(47, 114, 168, 0.8), rgba(243, 173, 145, 0.7)); // Green to orange gradient
  backdrop-filter: blur(8px);
  box-shadow: inset 0px 2px 5px rgba(255, 255, 255, 0.4), 0px 4px 20px rgba(76, 175, 80, 0.3); // Subtle glow around text
  display: inline-block;
  text-align: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  text-shadow: 0px 2px 4px rgba(76, 175, 80, 0.5); // Soft shadow for text depth

  @media (max-width: 768px) {
    padding: 20px 30px; // Adjust padding for tablets
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    padding: 15px 25px; // Further reduce padding for mobile
    font-size: 1rem;
    border-radius: 15px;
  }
`;

export const DiskButton = styled(Button)(({ state }) => ({
    background: state === 'ON'
        ? 'linear-gradient(145deg, #4CAF50, #81C784)' // Green for ON
        : state === 'OFF'
            ? 'linear-gradient(145deg,rgb(221, 230, 235),rgb(161, 168, 172))'
            : 'linear-gradient(145deg,rgb(221, 230, 235),rgb(161, 168, 172))',
    color: '#000',
    padding: '14px 28px',
    margin: '5px',
    borderRadius: '10px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
    transition: 'transform 0.2s ease, box-shadow 0.3s ease',
    fontWeight: 'bold',
    fontSize: '1rem',
    '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: state === 'ON'
            ? '0px 6px 12px rgba(150, 193, 199, 0.5)'
            : state === 'OFF'
                ? '0px 6px 12px rgba(96, 96, 96, 0.5)'
                : 'none',
    },
    '&:active': {
        transform: 'scale(0.95)',
    },

    // Responsive styling for tablet and mobile devices
    '@media (max-width: 768px)': {
        padding: '12px 24px',
        fontSize: '0.95rem',
    },
    '@media (max-width: 480px)': {
        padding: '10px 20px',
        fontSize: '0.85rem',
        borderRadius: '8px',
    },
}));

// DP2disk styled typography with a sleek silver effect
export const DiskTypography = styled(Typography)`
  color: #fff;
  font-weight: bold;
  padding: 15px 20px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: linear-gradient(135deg, rgba(75, 157, 168, 0.8), rgba(252, 164, 137, 0.6)); 
  backdrop-filter: blur(8px);
  box-shadow: inset 0px 2px 5px rgba(255, 255, 255, 0.4), 0px 4px 20px rgba(38, 198, 218, 0.3);
  display: inline-block;
  text-align: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  text-shadow: 0px 2px 4px rgba(38, 198, 218, 0.5);

  @media (max-width: 768px) {
    padding: 20px 30px; // Adjust padding for tablets
    font-size: 1.1rem;  // Slightly smaller text for tablets
  }

  @media (max-width: 480px) {
    padding: 15px 25px; // Further reduce padding for mobile
    font-size: 1rem;    // Even smaller text for mobile
    border-radius: 15px; // Adjust border radius for smaller screens
  }
`;

export const ResetButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #6a1b9a, #ff4081)',
    color: '#fff',
    padding: '12px 24px',
    fontSize: '18px',
    fontWeight: 'bold',
    borderRadius: '8px',
    textTransform: 'none',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease-in-out',
    marginTop: '5px',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        background: 'linear-gradient(135deg, #d500f9, #ff80ab)',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
        transform: 'scale(1.05)',
        cursor: 'pointer',
        '&::after': {
            transform: 'scale(10)',
            opacity: '0.3',
        },
    },
    '&:active': {
        transform: 'scale(0.98)',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '300%',
        height: '300%',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '50%',
        transition: 'all 0.6s ease-out',
        transform: 'translate(-50%, -50%) scale(0)',
        pointerEvents: 'none',
    },
}));

// SVG Icon (Reset)
export const ResetIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M12 4V1L8 5l4 4V6c3.86 0 7 3.14 7 7s-3.14 7-7 7-7-3.14-7-7h-2c0 4.41 3.59 8 8 8s8-3.59 8-8-3.59-8-8-8z" />
    </svg>
);

export const LampSVG = ({ state }) => (
    <svg
        width="30"
        height="50"
        viewBox="0 0 30 50"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle
            cx="15"
            cy="15"
            r="12"
            fill={state === 'green' ? '#4caf50' : state === 'red' ? '#f44336' : '#ffffff'}
        />
        <rect x="10" y="25" width="10" height="6" rx="3" fill="#8c8c8c" />
        <rect x="11" y="31" width="8" height="8" rx="4" fill="#b0b0b0" />
        {state === 'green' && (
            <line
                x1="15"
                y1="15"
                x2="15"
                y2="20"
                stroke="yellow"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        )}
    </svg>
);

export const LampSVGReset = ({ state }) => (
    <svg
        width="30"
        height="50"
        viewBox="0 0 30 50"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle
            cx="15"
            cy="15"
            r="12"
            fill={state === 'green' ? '#4caf50' : state === 'red' ? '#f44336' : '#ffffff'} // Adjust lamp color
        />
        <rect x="10" y="25" width="10" height="6" rx="3" fill="#8c8c8c" />
        <rect x="11" y="31" width="8" height="8" rx="4" fill="#b0b0b0" />
        {state === 'green' && (
            <line
                x1="15"
                y1="15"
                x2="15"
                y2="20"
                stroke="yellow"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        )}
    </svg>
);

export const LampSVGPumps = ({ state }) => (
    <svg
        width="30"
        height="50"
        viewBox="0 0 30 50"
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginLeft: '10px' }}
    >
        <circle
            cx="15"
            cy="15"
            r="12"
            fill={state === 'green' ? '#00FF00' : state === 'red' ? '#FF0000' : '#ffffff'}  // Brighter green and red
        />
        <rect x="10" y="25" width="10" height="6" rx="3" fill="#00BFFF" />
        <rect x="11" y="31" width="8" height="8" rx="4" fill="#00FA9A" />
        {state === 'green' && (
            <line
                x1="15"
                y1="15"
                x2="15"
                y2="20"
                stroke="yellow"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        )}
    </svg>
);

export const RefreshIcon = styled('img')({
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
      transform: 'rotate(360deg)',
    },
  });